/**
 * Payments Tab — real earnings summary + per-visit history (InstaPay).
 */

import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useRouter } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import {
    ActivityIndicator,
    RefreshControl,
    ScrollView,
    StyleSheet,
    TextInput,
    TouchableOpacity,
    View
} from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { BonusesCard } from "@/components/bonuses/BonusesCard";
import {
    fetchRnPayments,
    submitHoldExplanation,
    type RnPaymentHistoryItem,
    type RnPaymentNotice,
    type RnPaymentsResponse
} from "@/lib/api";
import { paymentRowKey } from "@/lib/payment-row-key";
import { colors, fonts, gradients, radius, shadows, spacing, typography } from "@/lib/theme";

function PinwheelLogo({ size = 34 }: { size?: number }) {
    const half = size / 2;
    return (
        <View style={{ width: size, height: size }}>
            <View
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.teal400,
                    borderTopLeftRadius: half,
                    borderBottomRightRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.pink400,
                    borderTopRightRadius: half,
                    borderBottomLeftRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.pink400,
                    borderTopRightRadius: half,
                    borderBottomLeftRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.teal400,
                    borderTopLeftRadius: half,
                    borderBottomRightRadius: half
                }}
            />
        </View>
    );
}

function formatCurrency(cents: number): string {
    return `$${(cents / 100).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })}`;
}

function formatClockIn(iso: string | null): string {
    if (!iso) {
        return "";
    }
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit"
    });
}

/**
 * Server-owned payments notice: a banking-holiday delay, or a manual override.
 *
 * This component decides nothing. It does not know what a holiday is, does not
 * compute a deposit date, and does not choose when to appear — the server sends
 * a notice or sends null. That is deliberate: hiding or rewording it is a
 * settings-row flip that takes effect on the next screen load, with no app
 * release and no OTA.
 *
 * The one local decision is `expiresAt`, so a phone that has been offline or
 * backgrounded cannot keep showing a delay that has already resolved.
 */
function PaymentNoticeBanner({ notice }: { notice: RnPaymentNotice }) {
    if (Date.parse(notice.expiresAt) <= Date.now()) {
        return null;
    }

    const tone =
        notice.severity === "warning"
            ? {
                  bg: "rgba(245,158,11,0.10)",
                  border: "rgba(245,158,11,0.35)",
                  fg: colors.warning500
              }
            : { bg: "rgba(59,130,246,0.10)", border: "rgba(59,130,246,0.35)", fg: colors.info500 };

    return (
        <View style={styles.noticeWrapper}>
            <View
                style={[styles.noticeCard, { backgroundColor: tone.bg, borderColor: tone.border }]}
            >
                <MaterialCommunityIcons
                    name={notice.severity === "warning" ? "calendar-alert" : "information-outline"}
                    size={20}
                    color={tone.fg}
                    style={styles.noticeIcon}
                />
                <View style={styles.noticeTextCol}>
                    <Text style={[styles.noticeTitle, { color: tone.fg }]}>{notice.title}</Text>
                    <Text style={styles.noticeBody}>{notice.body}</Text>
                </View>
            </View>
        </View>
    );
}

function statusBadgeColors(status: RnPaymentHistoryItem["paymentStatus"]): {
    bg: string;
    fg: string;
    label: string;
} {
    switch (status) {
        case "paid":
            return { bg: "rgba(16,185,129,0.12)", fg: colors.success500, label: "Paid" };
        case "in_batch":
            return { bg: "rgba(59,130,246,0.12)", fg: colors.info500, label: "Processing" };
        case "pending":
            return { bg: "rgba(245,158,11,0.12)", fg: colors.warning500, label: "Pending" };
        case "on_hold":
            return { bg: "rgba(244,63,94,0.12)", fg: "#f43f5e", label: "On hold" };
    }
}

/**
 * One held visit, with somewhere to say what happened.
 *
 * The copy is deliberately neutral. "We need to confirm the location" is true;
 * "your GPS says you weren't there" is an accusation, and on the September 2026
 * data it would have been the wrong one four times out of five — twice because
 * OUR address was wrong. She is being asked a question, not answering a charge.
 *
 * The SLA date is deliberately NOT printed here. The auto-release is a backstop
 * against our own inattention, not a term of her pay, and telling her "it pays
 * out on the 15th anyway" invites waiting out the clock instead of answering.
 * The timer still runs and she is still paid on it.
 */
function HeldVisitCard({
    item,
    onSaved
}: {
    item: RnPaymentHistoryItem;
    onSaved: () => Promise<void>;
}) {
    const [text, setText] = useState(item.holdExplanation ?? "");
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(item.holdExplanation != null);
    const [error, setError] = useState<string | null>(null);

    const trimmed = text.trim();
    const canSave = trimmed.length > 0 && trimmed !== (item.holdExplanation ?? "") && !saving;

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            await submitHoldExplanation(item.visitId, trimmed);
            setSaved(true);
            await onSaved();
        } catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't save that — try again");
        } finally {
            setSaving(false);
        }
    };

    return (
        <View style={styles.holdCard}>
            <View style={styles.holdHeader}>
                <MaterialCommunityIcons name="map-marker-alert-outline" size={18} color="#f43f5e" />
                <Text style={styles.holdPatient} numberOfLines={1}>
                    {item.patientName}
                </Text>
                <Text style={styles.holdAmount}>{formatCurrency(item.amountCents)}</Text>
            </View>

            {/* Deliberately does NOT quote the SLA date. The auto-release is a
                backstop against our own inattention, not an offer: printing
                "it pays out on the 15th anyway" invites her to wait out the
                clock instead of telling us what happened, and turns an internal
                safety net into a term of her pay. The timer still runs and she
                is still paid on it; she just is not being coached to ignore the
                question. */}
            <Text style={styles.holdBody}>
                We need to confirm the location for this visit before it&apos;s paid.
            </Text>

            <TextInput
                style={styles.holdInput}
                placeholder="What happened? e.g. I clocked out from my car after leaving"
                placeholderTextColor={colors.textTertiary}
                value={text}
                onChangeText={(v) => {
                    setText(v);
                    setSaved(false);
                }}
                multiline
                maxLength={2000}
                editable={!saving}
            />

            {error != null && <Text style={styles.holdError}>{error}</Text>}

            <View style={styles.holdActions}>
                {saved && !canSave ? (
                    <Text style={styles.holdSaved}>Saved — someone will review it</Text>
                ) : (
                    <View />
                )}
                <TouchableOpacity
                    activeOpacity={0.8}
                    disabled={!canSave}
                    onPress={() => void save()}
                    style={[styles.holdSaveButton, !canSave && styles.holdSaveButtonDisabled]}
                >
                    {saving ? (
                        <ActivityIndicator size="small" color="#FFFFFF" />
                    ) : (
                        <Text style={styles.holdSaveText}>Send</Text>
                    )}
                </TouchableOpacity>
            </View>
        </View>
    );
}

export default function PaymentsScreen() {
    const router = useRouter();
    const [data, setData] = useState<RnPaymentsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setError(null);
        try {
            const res = await fetchRnPayments();
            setData(res);
        } catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't load payments");
        }
    }, []);

    useEffect(() => {
        setLoading(true);
        load().finally(() => setLoading(false));
    }, [load]);

    const onRefresh = useCallback(async () => {
        setRefreshing(true);
        await load();
        setRefreshing(false);
    }, [load]);

    const summary = data?.summary ?? {
        thisWeekCents: 0,
        pendingCents: 0,
        allTimePaidCents: 0,
        onHoldCents: 0
    };
    const history = data?.history ?? [];
    const heldVisits = history.filter((h) => h.paymentStatus === "on_hold");

    return (
        <SafeAreaView style={styles.safeArea} edges={["top"]}>
            <LinearGradient
                colors={["rgba(69,210,176,0.08)", "rgba(231,72,245,0.03)", "rgba(255,255,255,0)"]}
                locations={[0, 0.12, 0.28]}
                style={styles.backgroundGradient}
            />

            <ScrollView
                style={styles.scrollView}
                contentContainerStyle={styles.scrollContent}
                showsVerticalScrollIndicator={false}
                refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
            >
                {/* Header */}
                <View style={styles.header}>
                    <PinwheelLogo size={42} />
                    <View style={styles.headerTextCol}>
                        <Text style={styles.headerTitle}>Payments</Text>
                        <Text style={styles.headerSub}>InstaPay — auto ACH next business day</Text>
                    </View>
                </View>

                {/* Server-owned notice — renders only when the API sends one */}
                {data?.notice ? <PaymentNoticeBanner notice={data.notice} /> : null}

                {/* Summary card */}
                <View style={styles.earningsCardWrapper}>
                    <View style={styles.earningsCard}>
                        <LinearGradient
                            colors={gradients.brand}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 0 }}
                            style={styles.earningsCardBorder}
                        />
                        <View style={styles.earningsTotalSection}>
                            <Text style={styles.earningsTotalAmount}>
                                {formatCurrency(summary.allTimePaidCents)}
                            </Text>
                            <Text style={styles.earningsTotalLabel}>All-Time Paid</Text>
                        </View>
                        <View style={styles.earningsDivider} />
                        <View style={styles.earningsColumns}>
                            <View style={styles.earningsColumn}>
                                <Text style={styles.earningsColumnLabel}>This Week</Text>
                                <Text style={styles.earningsColumnValue}>
                                    {formatCurrency(summary.thisWeekCents)}
                                </Text>
                            </View>
                            <View style={styles.earningsColumnSeparator} />
                            <View style={styles.earningsColumn}>
                                <Text style={styles.earningsColumnLabel}>Pending</Text>
                                <Text style={styles.earningsColumnValue}>
                                    {formatCurrency(summary.pendingCents)}
                                </Text>
                            </View>
                            {/* Only when there is something held. Held money is
                                deliberately NOT folded into Pending: without its own
                                column a nurse owed $180 reads "Pending $0.00" at the
                                top of her own Payments screen and has to scroll to
                                find out her money exists at all. */}
                            {summary.onHoldCents > 0 && (
                                <>
                                    <View style={styles.earningsColumnSeparator} />
                                    <View style={styles.earningsColumn}>
                                        <Text style={styles.earningsColumnLabel}>On hold</Text>
                                        <Text
                                            style={[
                                                styles.earningsColumnValue,
                                                styles.earningsColumnValueHeld
                                            ]}
                                        >
                                            {formatCurrency(summary.onHoldCents)}
                                        </Text>
                                    </View>
                                </>
                            )}
                        </View>
                    </View>
                </View>

                {/* Joining bonuses — renders only when the API sends something to show */}
                <BonusesCard bonuses={data?.bonuses} />

                {/* Payment statement — emails a per-month PDF of fees paid. */}
                <View style={styles.section}>
                    <TouchableOpacity
                        activeOpacity={0.7}
                        onPress={() => router.push("/payment-statement" as never)}
                        style={styles.statementRow}
                    >
                        <View style={styles.statementIcon}>
                            <MaterialCommunityIcons
                                name="file-document-outline"
                                size={20}
                                color={colors.teal600}
                            />
                        </View>
                        <View style={styles.statementTextCol}>
                            <Text style={styles.statementTitle}>Get payment statement</Text>
                            <Text style={styles.statementSub}>
                                Pick a month — we'll email you a PDF
                            </Text>
                        </View>
                        <MaterialCommunityIcons
                            name="chevron-right"
                            size={22}
                            color={colors.textTertiary}
                        />
                    </TouchableOpacity>
                </View>

                {/* On hold — visits waiting on a location check.
                    Given its own section above Recent Activity rather than left
                    as a badge in the list: a nurse whose money is late needs to
                    find out why without scrolling, and needs somewhere to say
                    what happened. */}
                {heldVisits.length > 0 && (
                    <View style={styles.section}>
                        <View style={styles.sectionHeader}>
                            <Text style={styles.sectionTitle}>On hold</Text>
                        </View>
                        {heldVisits.map((item) => (
                            <HeldVisitCard key={item.visitId} item={item} onSaved={load} />
                        ))}
                    </View>
                )}

                {/* Recent Activity */}
                <View style={styles.section}>
                    <View style={styles.sectionHeader}>
                        <Text style={styles.sectionTitle}>Recent Activity</Text>
                    </View>

                    {error ? (
                        <View style={styles.emptyState}>
                            <MaterialCommunityIcons
                                name="alert-circle-outline"
                                size={40}
                                color={colors.error500}
                            />
                            <Text style={styles.emptyTitle}>Couldn't load</Text>
                            <Text style={styles.emptySubtitle}>{error}</Text>
                        </View>
                    ) : loading && history.length === 0 ? (
                        <View style={styles.emptyState}>
                            <ActivityIndicator size="large" color={colors.teal400} />
                        </View>
                    ) : history.length === 0 ? (
                        <View style={styles.emptyState}>
                            <MaterialCommunityIcons
                                name="cash-multiple"
                                size={48}
                                color={colors.textTertiary}
                            />
                            <Text style={styles.emptyTitle}>No payments yet</Text>
                            <Text style={styles.emptySubtitle}>
                                Complete visits to start earning
                            </Text>
                        </View>
                    ) : (
                        <View style={styles.historyList}>
                            {history.map((item) => {
                                const badge = statusBadgeColors(item.paymentStatus);
                                const clockIn = formatClockIn(item.clockInAt);
                                return (
                                    <TouchableOpacity
                                        key={paymentRowKey(item)}
                                        activeOpacity={0.7}
                                        onPress={() =>
                                            router.push(`/visit/${item.visitId}` as never)
                                        }
                                        style={styles.historyRow}
                                    >
                                        <View style={styles.historyLeft}>
                                            <Text style={styles.historyPatient} numberOfLines={1}>
                                                {item.patientName}
                                            </Text>
                                            <Text style={styles.historyMeta} numberOfLines={1}>
                                                {item.agencyName}
                                            </Text>
                                            {clockIn.length > 0 && (
                                                <Text
                                                    style={styles.historyClockIn}
                                                    numberOfLines={1}
                                                >
                                                    Clocked in {clockIn}
                                                </Text>
                                            )}
                                            <View style={styles.historyBadges}>
                                                <View
                                                    style={[
                                                        styles.statusBadge,
                                                        { backgroundColor: badge.bg }
                                                    ]}
                                                >
                                                    <Text
                                                        style={[
                                                            styles.statusBadgeText,
                                                            { color: badge.fg }
                                                        ]}
                                                    >
                                                        {badge.label}
                                                    </Text>
                                                </View>
                                                {item.missedVisitPay && (
                                                    <View
                                                        style={[
                                                            styles.statusBadge,
                                                            {
                                                                backgroundColor:
                                                                    "rgba(245,158,11,0.12)"
                                                            }
                                                        ]}
                                                    >
                                                        <Text
                                                            style={[
                                                                styles.statusBadgeText,
                                                                { color: colors.warning500 }
                                                            ]}
                                                        >
                                                            Half (missed)
                                                        </Text>
                                                    </View>
                                                )}
                                                {item.payDifference === true && (
                                                    <View
                                                        style={[
                                                            styles.statusBadge,
                                                            { backgroundColor: colors.teal50 }
                                                        ]}
                                                    >
                                                        <Text
                                                            style={[
                                                                styles.statusBadgeText,
                                                                { color: colors.teal600 }
                                                            ]}
                                                        >
                                                            Pay difference
                                                        </Text>
                                                    </View>
                                                )}
                                            </View>
                                        </View>
                                        <Text style={styles.historyAmount}>
                                            {formatCurrency(item.amountCents)}
                                        </Text>
                                    </TouchableOpacity>
                                );
                            })}
                        </View>
                    )}
                </View>

                {/* CTA */}
                <View style={styles.ctaWrapper}>
                    <TouchableOpacity
                        activeOpacity={0.85}
                        onPress={() => router.push("/(tabs)")}
                        style={styles.ctaButtonOuter}
                    >
                        <LinearGradient
                            colors={gradients.brand}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 0 }}
                            style={styles.ctaButton}
                        >
                            <Text style={styles.ctaText}>Find Visits</Text>
                        </LinearGradient>
                    </TouchableOpacity>
                </View>
            </ScrollView>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    safeArea: { flex: 1, backgroundColor: colors.background },
    backgroundGradient: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 300
    },
    scrollView: { flex: 1 },
    scrollContent: { paddingBottom: spacing["2xl"] },

    header: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md,
        paddingHorizontal: spacing.base,
        paddingTop: spacing.base,
        paddingBottom: spacing.lg
    },
    headerTextCol: { flexDirection: "column" },
    headerTitle: {
        fontSize: 24,
        fontFamily: fonts.extraBold,
        color: colors.textPrimary
    },
    headerSub: {
        fontSize: 13,
        fontFamily: fonts.medium,
        color: colors.textTertiary,
        marginTop: 1
    },

    noticeWrapper: {
        paddingHorizontal: spacing.base,
        marginBottom: spacing.lg
    },
    noticeCard: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: spacing.sm,
        padding: spacing.base,
        borderRadius: radius.lg,
        borderWidth: 1.5
    },
    noticeIcon: { marginTop: 1 },
    noticeTextCol: { flex: 1 },
    noticeTitle: {
        fontSize: 14,
        fontFamily: fonts.semiBold,
        marginBottom: 3
    },
    noticeBody: {
        fontSize: 13,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        lineHeight: 18
    },

    earningsCardWrapper: {
        paddingHorizontal: spacing.base,
        marginBottom: spacing.xl
    },
    earningsCard: {
        backgroundColor: colors.surface,
        borderRadius: radius.xl,
        borderWidth: 1.5,
        borderColor: colors.border,
        overflow: "hidden",
        ...shadows.md
    },
    earningsCardBorder: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 3
    },
    earningsTotalSection: {
        alignItems: "center",
        paddingTop: spacing["2xl"],
        paddingBottom: spacing.lg
    },
    earningsTotalAmount: {
        fontSize: 28,
        fontFamily: fonts.extraBold,
        color: colors.textPrimary,
        letterSpacing: -0.3
    },
    earningsTotalLabel: {
        ...typography.caption,
        color: colors.textTertiary,
        marginTop: spacing.xs
    },
    earningsDivider: {
        height: 1,
        backgroundColor: colors.border,
        marginHorizontal: spacing.lg
    },
    earningsColumns: {
        flexDirection: "row",
        paddingVertical: spacing.base
    },
    earningsColumn: { flex: 1, alignItems: "center" },
    earningsColumnLabel: {
        ...typography.caption,
        color: colors.textTertiary,
        marginBottom: spacing.xs
    },
    earningsColumnValue: {
        fontSize: 18,
        fontFamily: fonts.bold,
        color: colors.textPrimary
    },
    earningsColumnValueHeld: {
        color: "#f43f5e"
    },
    earningsColumnSeparator: { width: 1, backgroundColor: colors.border },

    section: { paddingHorizontal: spacing.base },

    statementRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md,
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        borderWidth: 1,
        borderColor: colors.border,
        paddingVertical: spacing.md,
        paddingHorizontal: spacing.base,
        marginBottom: spacing.xl
    },
    statementIcon: {
        width: 38,
        height: 38,
        borderRadius: radius.full,
        backgroundColor: colors.teal50,
        alignItems: "center",
        justifyContent: "center"
    },
    statementTextCol: { flex: 1, gap: 2 },
    statementTitle: {
        fontSize: 14,
        fontFamily: fonts.semiBold,
        color: colors.textPrimary
    },
    statementSub: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.textTertiary
    },

    // ── On-hold card ──
    holdCard: {
        backgroundColor: "rgba(244,63,94,0.05)",
        borderColor: "rgba(244,63,94,0.18)",
        borderWidth: 1,
        borderRadius: radius.lg,
        padding: spacing.md,
        marginBottom: spacing.sm
    },
    holdHeader: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xs
    },
    holdPatient: {
        flex: 1,
        fontSize: 15,
        fontFamily: fonts.semiBold,
        color: colors.textPrimary
    },
    holdAmount: {
        fontSize: 15,
        fontFamily: fonts.semiBold,
        color: colors.textPrimary
    },
    holdBody: {
        marginTop: spacing.xs,
        fontSize: 13,
        fontFamily: fonts.regular,
        lineHeight: 19,
        color: colors.textSecondary
    },
    holdInput: {
        marginTop: spacing.sm,
        minHeight: 72,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: radius.md,
        backgroundColor: colors.surface,
        padding: spacing.sm,
        fontSize: 14,
        fontFamily: fonts.regular,
        color: colors.textPrimary,
        textAlignVertical: "top"
    },
    holdError: {
        marginTop: spacing.xs,
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.error500
    },
    holdActions: {
        marginTop: spacing.sm,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between"
    },
    holdSaved: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.success500
    },
    holdSaveButton: {
        minWidth: 84,
        alignItems: "center",
        justifyContent: "center",
        paddingVertical: spacing.xs,
        paddingHorizontal: spacing.md,
        borderRadius: radius.md,
        backgroundColor: colors.teal500
    },
    holdSaveButtonDisabled: {
        opacity: 0.4
    },
    holdSaveText: {
        fontSize: 14,
        fontFamily: fonts.semiBold,
        color: "#FFFFFF"
    },

    sectionHeader: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: spacing.base
    },
    sectionTitle: { ...typography.subtitle, color: colors.textPrimary },

    historyList: {
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        borderWidth: 1,
        borderColor: colors.border,
        overflow: "hidden"
    },
    historyRow: {
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: spacing.md,
        paddingVertical: spacing.md,
        paddingHorizontal: spacing.base,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: colors.border
    },
    historyLeft: { flex: 1, gap: spacing.xs },
    historyPatient: {
        fontSize: 14,
        fontFamily: fonts.semiBold,
        color: colors.textPrimary
    },
    historyMeta: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.textTertiary
    },
    historyClockIn: {
        fontSize: 11,
        fontFamily: fonts.medium,
        color: colors.textSecondary
    },
    historyBadges: {
        flexDirection: "row",
        gap: spacing.xs,
        marginTop: spacing.xs,
        flexWrap: "wrap"
    },
    statusBadge: {
        paddingHorizontal: spacing.sm,
        paddingVertical: 2,
        borderRadius: radius.full
    },
    statusBadgeText: {
        fontSize: 11,
        fontFamily: fonts.semiBold
    },
    historyAmount: {
        fontSize: 15,
        fontFamily: fonts.bold,
        color: colors.textPrimary,
        minWidth: 80,
        textAlign: "right"
    },

    emptyState: {
        alignItems: "center",
        paddingVertical: spacing["3xl"],
        gap: spacing.sm
    },
    emptyTitle: {
        ...typography.h2,
        color: colors.textPrimary,
        marginTop: spacing.md
    },
    emptySubtitle: {
        ...typography.body,
        color: colors.textSecondary,
        textAlign: "center",
        paddingHorizontal: spacing.lg
    },

    ctaWrapper: {
        paddingHorizontal: spacing.base,
        paddingTop: spacing.xl
    },
    ctaButtonOuter: {
        borderRadius: radius.full,
        ...shadows.brand
    },
    ctaButton: {
        borderRadius: radius.full,
        paddingVertical: spacing.base,
        alignItems: "center",
        justifyContent: "center"
    },
    ctaText: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: colors.surface
    }
});
